import React, { useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';
import { emailSchema, passwordSchema } from '@/lib/validation';
import { APP_URL } from '@/lib/config';
import { AuthContext } from './auth-context';

export { useAuth } from './auth-context';


export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { logLoginAttempt, logSecurityEvent } = useSecurityAudit();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event);
        
        if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
        }
        
        if (event === 'PASSWORD_RECOVERY') {
          try { sessionStorage.setItem('in_password_recovery', '1'); } catch {}
        }
        
        if (event === 'SIGNED_IN' && session) {
          try { sessionStorage.setItem('had_session', '1'); } catch {}
        }
        
        if (event === 'SIGNED_OUT') {
          try {
            sessionStorage.removeItem('had_session');
            sessionStorage.removeItem('in_password_recovery');
          } catch {}
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Erro ao verificar sessão:', error);
        setUser(null);
        setSession(null);
        setLoading(false);
        return;
      }

      if (!session) {
        setUser(null);
        setSession(null);
        setLoading(false);
        return;
      }

      try { sessionStorage.setItem('had_session', '1'); } catch {}
      setSession(session);
      setUser(session.user);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Verificar e renovar sessão periodicamente
  useEffect(() => {
    if (!user) return;

    const checkAndRefreshSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Erro ao verificar sessão:', error);
        return;
      }
      
      // Se sessão está próxima de expirar (menos de 5 minutos), forçar refresh
      if (session?.expires_at) {
        const expiresIn = session.expires_at * 1000 - Date.now();
        if (expiresIn < 5 * 60 * 1000) {
          console.log('Sessão próxima de expirar, renovando...');
          const { error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            console.error('Erro ao renovar sessão:', refreshError);
          }
        }
      }
    };

    // Verificar sessão a cada 5 minutos
    const intervalId = setInterval(checkAndRefreshSession, 5 * 60 * 1000);
    
    // Verificar imediatamente ao montar
    checkAndRefreshSession();

    return () => clearInterval(intervalId);
  }, [user]);

  const signUp = async (email: string, password: string, fullName?: string) => {
    // Validate input
    const emailValidation = emailSchema.safeParse(email);

    if (!emailValidation.success) {
      return { error: { message: emailValidation.error.errors[0]?.message || 'Email inválido' } };
    }

    const passwordValidation = passwordSchema.safeParse(password);

    if (!passwordValidation.success) {
      return { error: { message: passwordValidation.error.errors[0]?.message || 'Senha inválida' } };
    }

    const redirectUrl = `${APP_URL}/auth?flow=signup`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        // Sem isso o perfil nasce com nome_completo = 'Usuário'.
        data: fullName?.trim() ? { full_name: fullName.trim() } : undefined,
      }
    });

    // Quando o email já tem conta confirmada, o Supabase (por design, pra não
    // vazar quais emails existem) retorna sucesso SEM erro e SEM enviar email,
    // mas com `data.user.identities` como array vazio. Detectamos isso para
    // mostrar a mensagem certa em vez da falsa promessa de email de confirmação.
    const alreadyRegistered =
      !error &&
      !!data?.user &&
      Array.isArray(data.user.identities) &&
      data.user.identities.length === 0;

    // Log security event
    await logSecurityEvent({
      event_type: 'signup_attempt',
      event_details: { 
        email: email.substring(0, 3) + '***',
        success: !error && !alreadyRegistered
      }
    });
    
    return { error, alreadyRegistered };
  };

  const signIn = async (email: string, password: string) => {
    // Validate email format
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      return { error: { message: 'Email inválido' } };
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Log login attempt
    await logLoginAttempt(!error, email);
    
    return { error };
  };

  const signOut = async () => {
    // Log logout
    await logSecurityEvent({
      event_type: 'logout',
      event_details: { user_id: user?.id }
    });
    
    try { sessionStorage.removeItem('had_session'); } catch {}
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    // Validate email
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      return { error: { message: 'Email inválido' } };
    }

    const redirectUrl = `${APP_URL}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    
    // Log password reset attempt
    await logSecurityEvent({
      event_type: 'password_reset_request',
      event_details: { 
        email: email.substring(0, 3) + '***',
        success: !error
      }
    });
    
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
